import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import { presentValueOr } from '../../../utils';
import { nil, TransitionStatus } from '../../public/Dialog/generic/utilities';

export type DialogPageOptions = {
  title: string;
  disableNext?: (() => boolean) | (() => Promise<boolean>);
};

type DialogContextType = {
  registerPage: (pageId: string, pageOptions: DialogPageOptions) => number;
  unregisterPage: (pageId: string) => void;
  isNextDisabled: () => Promise<boolean>;
  goToPage: (pageNumber: number) => void;

  pages: DialogPage[];

  currentPage: number;
  previousPage: number;
  transitionStatus: TransitionStatus;
  stepperArray: {
    name: string;
    condition?: (() => boolean) | (() => Promise<boolean>);
  }[];

  handleBack: () => void;
  handleNext: () => void;

  onEntered: () => void;
  onExited: () => void;
};

const DialogContext = createContext<DialogContextType | undefined>(undefined);

type DialogProviderProps = {
  children: ReactNode;
  initialPage?: number;
};

export type DialogPage = {
  id: string;
  options: DialogPageOptions;
};

let pages: DialogPage[] = [];

const idToPageNumber: Record<string, number> = {};

export const DialogProvider: React.FC<DialogProviderProps> = ({
  children,
  initialPage,
}) => {
  const [statePages, setStatePages] = useState<DialogPage[]>(pages);

  const registerPage = useCallback(
    (pageId: string, pageOptions: DialogPageOptions) => {
      const previouslyAssignedPageNumber = idToPageNumber[pageId];
      const pageNumber = presentValueOr(
        previouslyAssignedPageNumber,
        pages.length
      );

      const newPage = {
        id: pageId,
        options: pageOptions,
      };

      if (nil(previouslyAssignedPageNumber)) {
        pages = [...pages, newPage];
      } else {
        pages = [
          ...pages.slice(0, pageNumber),
          newPage,
          ...pages.slice(pageNumber),
        ];
      }

      idToPageNumber[pageId] = pageNumber;

      setStatePages(pages);

      return pageNumber;
    },
    [setStatePages]
  );

  const unregisterPage = useCallback(
    (pageId: string) => {
      pages = pages.filter(page => page.id !== pageId);
      setStatePages(pages);
    },
    [setStatePages]
  );

  const [currentPage, setCurrentPage] = useState(initialPage || 0);
  const [previousPage, setPreviousPage] = useState((initialPage || 0) - 1);
  const [transitionStatus, setTransitionStatus] = useState<TransitionStatus>(
    TransitionStatus.IDLE
  );

  const isNextDisabled = async (page?: number) => {
    const pageToCheck = page !== undefined ? page : currentPage;
    const currentPageData = statePages[pageToCheck];
    if (await currentPageData?.options.disableNext?.()) {
      return true;
    }
    return false;
  };

  const handleBack = useCallback(() => {
    setPreviousPage(currentPage);
    setCurrentPage(currentPage - 1);
    setTransitionStatus(TransitionStatus.EXITING);
  }, [currentPage, setCurrentPage, setPreviousPage, setTransitionStatus]);

  const handleNext = useCallback(async () => {
    if (await isNextDisabled(currentPage)) {
      console.log(
        'Next is disabled for current page, cannot proceed to next page.'
      );
      return;
    }

    setPreviousPage(currentPage);
    setCurrentPage(currentPage + 1);
    setTransitionStatus(TransitionStatus.EXITING);
  }, [
    currentPage,
    setCurrentPage,
    setPreviousPage,
    setTransitionStatus,
    statePages,
  ]);

  const isNextDisabledInRequestedRange = useCallback(
    async (targetPage: number) => {
      const start = Math.min(currentPage, targetPage);
      const end = Math.max(currentPage, targetPage);

      for (let page = start; page < end; page++) {
        if (await isNextDisabled(page)) {
          return true;
        }
      }
      return false;
    },
    [currentPage, isNextDisabled]
  );

  const goToPage = useCallback(
    async (pageNumber: number) => {
      if (
        pageNumber < 0 ||
        pageNumber >= statePages.length ||
        pageNumber === currentPage
      ) {
        return;
      }

      if (await isNextDisabledInRequestedRange(pageNumber)) {
        return;
      }

      setPreviousPage(currentPage);
      setCurrentPage(pageNumber);
      setTransitionStatus(TransitionStatus.EXITING);
    },
    [currentPage, statePages.length]
  );

  const onEntered = useCallback(() => {
    setTransitionStatus(TransitionStatus.IDLE);
  }, [setTransitionStatus]);

  const onExited = useCallback(() => {
    if (transitionStatus === TransitionStatus.EXITING) {
      setTransitionStatus(TransitionStatus.ENTERING);
    } else {
      setTransitionStatus(TransitionStatus.IDLE);
    }
  }, [setTransitionStatus, transitionStatus]);

  const projectedStepperArray = pages.map(page => ({
    name: page.options.title,
    condition: page.options.disableNext,
  }));

  return (
    <DialogContext.Provider
      value={{
        registerPage,
        unregisterPage,
        isNextDisabled,
        goToPage,
        pages,
        currentPage,
        previousPage,
        transitionStatus,
        handleBack,
        handleNext,
        onEntered,
        onExited,
        stepperArray: projectedStepperArray,
      }}
    >
      {children}
    </DialogContext.Provider>
  );
};

export function useDialogContext(): DialogContextType {
  const context = useContext(DialogContext);
  if (!context) {
    // throw new Error('useDialogContext must be used within a DialogProvider');
    console.warn('useDialogContext must be used within a DialogProvider');
    return {
      goToPage: () => {},
      handleBack: () => {},
      handleNext: () => {},
      isNextDisabled: async () => false,
      onEntered: () => {},
      onExited: () => {},
      pages: [],
      previousPage: 0,
      registerPage: () => 0,
      transitionStatus: TransitionStatus.IDLE,
      unregisterPage: () => {},
      currentPage: 0,
      stepperArray: [],
    };
  }
  return context;
}

export function withDialogProvider<P extends object>(
  Component: React.ComponentType<P>
) {
  const Wrapped: React.FC<P> = props => (
    <DialogProvider>
      <Component {...props} />
    </DialogProvider>
  );

  Wrapped.displayName = `withDialogProvider(${Component.displayName ?? Component.name ?? 'Component'})`;
  return Wrapped;
}
